/* eslint-disable @typescript-eslint/no-use-before-define */
import { useEffect, useRef, useState } from 'react';
import Pie, { ProvidedProps, PieArcDatum } from '@visx/shape/lib/shapes/Pie';
import { scaleOrdinal } from '@visx/scale';
import { Group } from '@visx/group';
import styles from './donut-graph.module.scss';
import { arc as d3Arc } from 'd3-shape';
import { interpolate as d3Interpolate, select as d3Select } from 'd3';
import { formatToMaxFourDigits } from '@flexo/general';
import { useThrottledHaptics, useThrottledTouch } from '@flexo/hooks';
import { useOnClickOutside } from '@flexo/hooks';
import { App as CapApp } from '@capacitor/app';

const ENV = (import.meta as any).env;

export type PieProps = {
  width: number;
  height: number;
  defaultMargin?: { top: number; right: number; bottom: number; left: number };
  animate?: boolean;
  graphData: any;
  colors: string[];
  totalAmount: any;
  totalAmounttype: string;
  keys?: any;
  selectedId: string | null;
  setSelectedId: any;
  id?: string;
  dataInTimeSpan?: boolean;
  isCommunityStats?: boolean;
  debugTouchArea?: boolean;
};

export function DonutGraph({
  width,
  height,
  animate = true,
  graphData,
  keys,
  colors,
  totalAmount,
  totalAmounttype,
  selectedId,
  setSelectedId,
  dataInTimeSpan = true,
  id,
  isCommunityStats = false,
  debugTouchArea = false,
}: PieProps) {
  const { DonutGraph__TotalAmount, DonutGraph__TotalAmount__Type } = styles;
  const [selected, setSelected] = useState<any | null>(null);
  const vibrate = useThrottledHaptics();

  const allValuesAreZero = graphData.every(
    (item) => item.value === 0 || item.value < 0 || item.value === '-.-'
  );

  const pieRef = useRef(null);

  const value = (d) => d.value;
  const orderedColors = [...colors];

  const getSiteDataColor = scaleOrdinal({
    domain: keys,
    range: allValuesAreZero ? ['#f0f0f0'] : orderedColors,
  });

  if (allValuesAreZero) {
    graphData = [
      {
        label: 'No Data',
        value: 100,
        formattedValue: '0',
      },
    ];
  }

  const defaultMargin = { top: 20, right: 0, bottom: 20, left: 0 };

  if (width < 10) return null;

  const innerWidth = width - defaultMargin.left - defaultMargin.right;
  const innerHeight = height - defaultMargin.top - defaultMargin.bottom;
  const radius = Math.min(innerWidth, innerHeight) / 1.5;
  const centerY = innerHeight / 2;
  const centerX = innerWidth / 2;
  const donutThickness = 7;

  const getfilteredData = () => {
    if (selected) {
      if (isCommunityStats) {
        return graphData.filter(
          (d: any) =>
            d?.label === selected?.data?.label || d?.id === selected?.data?.id
        );
      }
      return graphData.filter(({ item }: any) => item === selected.label);
    } else if (selectedId) {
      if (isCommunityStats) {
        return graphData.filter(
          (d: any) => d?.id === selectedId || d?.label === selectedId
        );
      }
      return graphData.filter((item: any) => item.label === selectedId);
    }
    return graphData;
  };

  // For CommunityStats, the visible donut should show the proportion of the "other"
  // value relative to the "main" value, not relative to the sum. This ensures that
  // if main=0.82 and other=0.41, the highlight covers ~50% of the ring.
  const getVisibleData = () => {
    const filtered = getfilteredData();
    if (!isCommunityStats) return filtered;

    if (!Array.isArray(filtered) || filtered.length < 2) return filtered;

    const mainValue = Number(filtered?.[1]?.value ?? 0);
    const otherValue = Number(filtered?.[0]?.value ?? 0);

    if (!(mainValue > 0)) return filtered;

    const ratio = Math.min(Math.max(otherValue / mainValue, 0), 1);
    const percent = ratio * 100;

    const highlighted = {
      ...filtered[0],
      value: percent,
      formattedValue: percent,
    };
    const remainder = {
      ...filtered[1],
      value: 100 - percent,
      formattedValue: 100 - percent,
    };

    return [highlighted, remainder];
  };




  // Compute the value displayed in the donut center in a clear, readable way
  let displayTotalAmount: any = totalAmount;
  if (allValuesAreZero) {
    displayTotalAmount = '-.-';
  } else if (selected !== null) {
    const formatted = formatToMaxFourDigits(selected?.data?.formattedValue);
    displayTotalAmount = formatted === '-.-' ? 0 : formatted;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useOnClickOutside(pieRef, (event: any) => {
    if (!selected) return;
    if (event.target.tagName === 'SPAN') return;
    else {
      setSelected(null);
      setSelectedId(null);
    }
  });
  return (
    <div
      style={{
        position: 'relative',
        width: width,
        height: height,
        display: 'flex',
        justifyContent: 'flex-start',
        backgroundColor: 'transparent',
      }}
      ref={pieRef}
    >
      <div
        style={{
          pointerEvents: 'none',
          position: 'absolute',
          top: centerY,
          width: innerWidth,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
        }}
      >
        <span className={`${DonutGraph__TotalAmount} heading1L`}>
          {displayTotalAmount}
        </span>
        <span className={`${DonutGraph__TotalAmount__Type} detail`}>
          {totalAmounttype}
        </span>
      </div>
      <svg
        width={width}
        height={height}
        overflow="visible"
        style={{
          pointerEvents: isCommunityStats ? 'none' : 'auto'
        }}
      >
        <rect rx={14} width={width} height={height} fill="transparent" />

        <Group
          top={centerY + defaultMargin.top}
          left={centerX + defaultMargin.left}
        >
          {/* Background circle for when isCommunityStats is true */}
          {isCommunityStats && (
            <circle
              r={radius - donutThickness / 2}
              cx={0}
              cy={0}
              fill="transparent"
              stroke={orderedColors[1] || '#e8f5e9'}
              strokeWidth={donutThickness}
              opacity={1}
            />
          )}

          {
            /* Visible Pie Chart */
            // Use normalized data for CommunityStats to render highlight as (other/main)
            // while preserving the original data for interactions (transparent pie below).
          }
          <Pie
            data={getVisibleData()}
            pieValue={value}
            outerRadius={radius}
            innerRadius={radius - donutThickness}
            cornerRadius={4}
            padAngle={isCommunityStats ? 0 : 0.03}
            pieSort={null}
          >
            {(pie) => (
              <AnimatedPie
                {...pie}
                radius={radius}
                donutThickness={donutThickness}
                getKey={(arc) => arc.data?.label} // Ensure this is passed correctly
                getColor={(arc) => getSiteDataColor(arc.data?.label)}
                selected={selected}
                animate={animate}
                setSelectedId={setSelectedId}
                selectedId={selectedId}
                setSelected={setSelected}
                allValuesAreZero={allValuesAreZero}
                isTransparent={false}
                isCommunityStats={isCommunityStats}
                vibrate={vibrate}
              />
            )}
          </Pie>

          {/* Transparent Pie Chart with Larger Clickable Area */}
          <Pie
            data={graphData}
            pieValue={value}
            outerRadius={radius + 100}
            innerRadius={radius - donutThickness - 50}
            cornerRadius={3}
            padAngle={isCommunityStats ? 0 : 0.03}
            pieSort={null}
          >
            {(pie) => (
              <>
                {}
                <AnimatedPie
                  {...pie}
                  animate={animate}
                  selected={selected}
                  radius={radius}
                  donutThickness={50}
                  getColor={(arc) =>
                    debugTouchArea ? 'rgba(255, 0, 0, 0.2)' : 'transparent'
                  }
                  getKey={(arc) => arc.data?.label}
                  setSelectedId={setSelectedId}
                  setSelected={setSelected}
                  selectedId={selectedId}
                  allValuesAreZero={allValuesAreZero}
                  isTransparent={!debugTouchArea}
                  isCommunityStats={isCommunityStats}
                  vibrate={vibrate}
                />
              </>
            )}
          </Pie>
        </Group>
      </svg>
    </div>
  );
}

type AnimatedPieProps<Datum> = ProvidedProps<Datum> & {
  animate?: boolean;
  getKey: (d: PieArcDatum<Datum>) => string;
  getColor: (d: PieArcDatum<Datum>) => string;
  delay?: number;
  selected: any;
  radius: number;
  donutThickness: number;
  setSelected: any;
  selectedId: any;
  setSelectedId: any;
  allValuesAreZero: boolean;
  isTransparent: boolean;
  isCommunityStats: boolean;
  vibrate: (duration?: number) => void;
};

function ArcComponent({
  arc,
  isSelected,
  radius,
  popOutDistance,
  donutThickness,
  getColor,
  getKey,
  setSelected,
  setSelectedId,
  animate,
  selectedId,
  isTransparent,
  isCommunityStats,
  onMobileClick,
}: any) {
  const arcRef = useRef(null);
  const lastSelectedRef = useRef(isSelected);
  const [currentArc, setCurrentArc] = useState(arc);
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (!arcRef.current || !isMountedRef.current) return;

    const arcGenerator = d3Arc()
      .outerRadius(currentOuterRadius)
      .innerRadius(currentInnerRadius)
      .cornerRadius(2);

    const interpolateStartAngle = d3Interpolate(
      currentArc.startAngle,
      arc.startAngle
    );
    const interpolateEndAngle = d3Interpolate(
      currentArc.endAngle,
      arc.endAngle
    );

    // CRITICAL: Interrupt any ongoing transition before starting new one
    const selection = d3Select(arcRef.current);
    const wasInterrupted = selection.interrupt('arcChange');

    // Transition for arc value changes based on calendar selector (using named transition "arcChange")
    selection
      .transition('arcChange')
      .duration(500)
      .attrTween('d', function () {
        return function (t) {
          const interpolatedArc = {
            ...arc, // this is to ensure we don't miss other properties
            startAngle: interpolateStartAngle(t),
            endAngle: interpolateEndAngle(t),
          };
          return arcGenerator(interpolatedArc);
        };
      })
      .on('end', () => {
        if (isMountedRef.current) {
          setCurrentArc(arc);
        }
      });

    return () => {
      if (arcRef.current) {
        d3Select(arcRef.current).interrupt('arcChange');
      }
    };
  }, [arc]);

  useEffect(() => {
    if (!arcRef.current || !isMountedRef.current) return;

    if (!isSelected) return;
    if (!animate) {
      // If there's no animation, simply set the arcRef with its dimensions immediately
      const arcGenerator = d3Arc()
        .outerRadius(isSelected ? radius + popOutDistance : radius)
        .innerRadius(
          isSelected
            ? radius - donutThickness + popOutDistance
            : radius - donutThickness
        )
        .cornerRadius(2);

      d3Select(arcRef.current).attr('d', arcGenerator(arc as any) || '');
      return; // Exit the useEffect early
    }

    const initialOuterRadius = lastSelectedRef.current
      ? radius + popOutDistance
      : radius;
    const initialInnerRadius = lastSelectedRef.current
      ? radius - donutThickness + popOutDistance
      : radius - donutThickness;

    const finalOuterRadius = isSelected ? radius + popOutDistance : radius;
    const finalInnerRadius = isSelected
      ? radius - donutThickness + popOutDistance
      : radius - donutThickness;

    // CRITICAL: Interrupt any ongoing selection transition before starting new one
    const selection = d3Select(arcRef.current);
    selection.interrupt();

    selection
      .transition()
      .duration(300)
      .attrTween('d', function () {
        const interpolateOuter = d3Interpolate(
          initialOuterRadius,
          finalOuterRadius
        );
        const interpolateInner = d3Interpolate(
          initialInnerRadius,
          finalInnerRadius
        );
        return function (t) {
          const arcGenerator = d3Arc()
            .outerRadius(interpolateOuter(t))
            .innerRadius(interpolateInner(t))
            .cornerRadius(2);
          return arcGenerator(arc as any) || '';
        };
      });

    lastSelectedRef.current = isSelected;

    return () => {
      if (arcRef.current) {
        d3Select(arcRef.current).interrupt();
      }
    };
  }, [isSelected]);

  // Component unmount cleanup
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (arcRef.current) {
        const selection = d3Select(arcRef.current);
        selection.interrupt('arcChange');
        selection.interrupt();
      }
    };
  }, []);

  const currentOuterRadius = isSelected ? radius + popOutDistance : radius;
  const currentInnerRadius = isSelected
    ? radius - donutThickness + popOutDistance
    : radius - donutThickness;

  const arcGenerator = d3Arc()
    .outerRadius(currentOuterRadius)
    .innerRadius(currentInnerRadius)
    .cornerRadius(2);

  // Web interaction handler - KEEP AS IS (working correctly)
  const onWebHover = (arc, event) => {
    if (isCommunityStats) return; // Disable hover for community stats
    if (ENV?.VITE_APP_IS_WEB === 'false') return;
    const newId = arc.data?.id;
    if (arc.data.label === 'No Data' || arc.data.value === 0) return;
    if (!animate) return;

    if (event.type === 'mouseenter') {
      if (selectedId !== newId) {
        setSelectedId(newId);
      }
    } else if (event.type === 'mouseleave') {
      if (selectedId !== null) {
        setSelectedId(null);
      }
    }
  };

  return (
    <g key={getKey(arc)}>
      <path
        ref={arcRef}
        d={arcGenerator(currentArc as any) || ''}
        fill={getColor(arc)}
        // onTouchStart={(event) => onMobileTouch(arc, event)}
        // onTouchEnd={(event) => onMobileTouch(arc, event)}
        // onTouchCancel={(event) => onMobileTouch(arc, event)}
        onClick={(event) => onMobileClick(arc, event)}
        onMouseEnter={(event) => onWebHover(arc, event)}
        onMouseLeave={(event) => onWebHover(arc, event)}
        style={{
          WebkitTapHighlightColor: 'transparent',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
        }}
      />
    </g>
  );
}

function AnimatedPie<Datum>({
  arcs,
  getKey,
  getColor,
  selected,
  radius,
  donutThickness,
  setSelected,
  setSelectedId,
  selectedId,
  animate,
  isTransparent,
  isCommunityStats,
  vibrate,
}: AnimatedPieProps<Datum | any>) {
  const popOutDistance = 10;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHandlingClick = useRef(false);
  const shouldAllowTouch = useThrottledTouch(350);
  const isMountedRef = useRef(true);
  const appStateListenerRef = useRef<any>(null);

  useEffect(() => {
    if (selectedId !== null) {
      const matchedArc = arcs.find((arc) => arc.data?.id === selectedId);
      setSelected(matchedArc || null);
    } else {
      setSelected(null);
    }
  }, [selectedId, setSelected, selected?.data?.id]);

  // Clear timeout when selectedId changes externally (e.g., from touching a bar)
  // OR when selection is cleared (selectedId becomes null)
  useEffect(() => {
    // Skip clearing if the change came from handleMobileClick
    if (isHandlingClick.current) {
      isHandlingClick.current = false;
      return;
    }

    // Clear timeout for external changes (touching a bar or clearing selection)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [selectedId]);

  // Centralized mobile click handler with proper timeout management
  const handleMobileClick = (arc, event) => {
    // For community stats, don't handle clicks - let parent handle touch events
    if(isCommunityStats) return;

    if (!isMountedRef.current) {
      console.warn('[DonutGraph] ⚠️ Interaction blocked - component unmounted');
      return;
    }
    if (!shouldAllowTouch()) {
      return; // Throttled (expected, don't spam logs)
    }

    event.preventDefault();
    event.stopPropagation();

    const newId = arc.data?.id;
    if (arc.data.label === 'No Data' || arc.data.value === 0) return;
    if (!animate) return;

    // Clear any existing timeout before setting a new one
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Set flag to indicate this is an internal change
    isHandlingClick.current = true;

    // Set the new selection
    setSelectedId(newId);
    vibrate();

    // Set new timeout to clear selection
    timeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setSelectedId(null);
      }
      timeoutRef.current = null;
    }, 3000);
  };

  // Handle app background - clear timeouts
  useEffect(() => {
    const handleAppStateChange = ({ isActive }: { isActive: boolean }) => {
      if (!isActive) {
        console.log('[DonutGraph] 📱 App going to background - clearing state');
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (isMountedRef.current) {
          setSelectedId(null);
        }
      } else {
        console.log('[DonutGraph] 📱 App resumed to foreground');
      }
    };

    CapApp.addListener('appStateChange', handleAppStateChange).then((listener) => {
      appStateListenerRef.current = listener;
    });

    return () => {
      if (appStateListenerRef.current) {
        appStateListenerRef.current.remove();
      }
    };
  }, [setSelectedId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      console.log('[DonutGraph] 🔄 Component unmounting - cleanup');
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  // Filter arcs based on selection state
  const visibleArcs = !isCommunityStats
    ? arcs
    : selected
    ? arcs.filter((arc) => getKey(arc) === getKey(selected))
    : arcs;

  return (
    <>
      {visibleArcs.map((arc) => {
        const isSelected = selected && getKey(arc) === getKey(selected);
        return (
          <ArcComponent
            arc={arc}
            isSelected={isSelected}
            radius={radius}
            popOutDistance={popOutDistance}
            donutThickness={donutThickness}
            getColor={getColor}
            getKey={getKey}
            setSelected={setSelected}
            animate={animate}
            key={getKey(arc)}
            setSelectedId={setSelectedId}
            selectedId={selectedId}
            isTransparent={isTransparent}
            isCommunityStats={isCommunityStats}
            onMobileClick={handleMobileClick}
          />
        );
      })}
    </>
  );
}

export default DonutGraph;
